import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { deletePageTagMutation } from '@/graphql/mutations/deletePageTag';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface DeletePageTagModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  pageId: string;
  tagId: string;
  tagName?: string;
  onSuccess?: () => void;
}

const DeletePageTagModal: React.FC<DeletePageTagModalProps> = ({
  isOpen,
  onOpenChange,
  slug,
  pageId,
  tagId,
  tagName = 'this tag',
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleDelete = async () => {
    if (!tagId) return;
    setIsSubmitting(true);
    try {
      const response = await deletePageTagMutation(slug, pageId, tagId);
      if (response.deletePageTag.success) {
        toast({
          title: 'Tag removed',
          description: `Successfully removed ${tagName}`,
        });
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } else {
        toast({
          title: 'Error',
          description: response.deletePageTag.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting page tag:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Remove Tag
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove {tagName}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isSubmitting ? 'Removing...' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeletePageTagModal;
